import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { getStudentPlanMeta } from '../lib/firestore/studentPlan';
import { generateTermPlan } from '../lib/api';
import { saveStudentPlanMeta, logAuditEvent } from '../lib/firestore/studentPlan';
import { useTriageStore } from '../lib/stores/triageStore';
import BranchingQuestionnaireForm from '../components/BranchingQuestionnaireForm';
import AppLayout from '../components/AppLayout';
import toast from 'react-hot-toast';

const TriagePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { reset } = useTriageStore();

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        setLoading(true);
        
        const studentRef = doc(db, 'students', id);
        const studentSnap = await getDoc(studentRef);
        
        if (!studentSnap.exists()) {
          toast.error('Student not found');
          navigate('/students');
          return;
        }
        
        setStudent({ id: studentSnap.id, ...studentSnap.data() });
        
        // Reset triage store for new assessment
        reset();
        
      } catch (error) {
        console.error('Error fetching student:', error);
        toast.error('Error loading student data');
      } finally {
        setLoading(false);
      }
    };

    fetchStudent();
  }, [id, navigate, reset]);

  const handleSubmit = async (questionnaireData) => {
    try {
      setSubmitting(true);
      
      // Use the new callable function (automatically handles auth)
      const result = await generateTermPlan(id);

      // Import questionnaire logic functions for saving metadata
      const { deriveAbilityTiers, mapAnswersToInterventionTags } = require('../lib/questionnaireLogic');
      
      // Derive ability profile and tags for metadata
      const abilityProfile = deriveAbilityTiers(questionnaireData.branchAnswers);
      const { focusTags, needAreas } = mapAnswersToInterventionTags(questionnaireData.branchAnswers, questionnaireData.currentBranch);
      
      // Save to student plan meta
      const planMeta = {
        ncStatus: questionnaireData.ncStatus,
        questionnaireAnswers: {
          ncStatusAnswer: questionnaireData.ncStatus,
          branchAnswers: questionnaireData.branchAnswers
        },
        abilityProfile,
        focusTags,
        needAreas,
        decisionSource: 'QUESTIONNAIRE',
        planGenerationResult: result, // Store the plan generation result
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedByUid: user?.uid
      };
      
      await saveStudentPlanMeta(id, planMeta);
      
      // Log audit event
      await logAuditEvent(id, {
        action: 'QUESTIONNAIRE_COMPLETED',
        details: {
          ncStatus: questionnaireData.ncStatus,
          planType: result.planType || 'primary',
          triagePath: result.triagePath,
          branch: questionnaireData.currentBranch,
          abilityProfile,
          focusTags,
          needAreas
        },
        timestamp: new Date().toISOString(),
        userId: user?.uid
      });
      
      toast.success('Assessment completed successfully!');
      
      // Route based on plan type and triage path
      if (result.planType === 'secondary_interventions') {
        // Navigate to secondary interventions view
        navigate(`/student/${id}/secondary-interventions`, { 
          state: { 
            planData: result.plan,
            planType: 'secondary_interventions'
          } 
        });
      } else if (result.triagePath === 'off_national_individualised') {
        // Navigate to teaching plan view with tiles for below-NC students
        navigate(`/students/${id}/teaching-plan`);
      } else if (result.triagePath === 'national_interventions') {
        // Navigate to interventions-only view for at/near NC students
        navigate(`/student/${id}/interventions-only`, { 
          state: { 
            planData: result.plan,
            planType: 'national_interventions'
          } 
        });
      } else {
        // Fallback navigation
        navigate(`/students/${id}/teaching-plan`);
      }
      
    } catch (error) {
      console.error('Error submitting questionnaire:', error);
      toast.error('Failed to save assessment: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate(`/student/${id}`);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  if (!student) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Student not found</h2>
          <button
            onClick={() => navigate('/students')}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Back to Students
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Assessment for {student.firstName} {student.lastName}
              </h1>
              <p className="text-gray-600">
                Complete the questionnaire to determine the best support approach for this student.
              </p>
            </div>
            <button
              onClick={handleBack}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Profile
            </button>
          </div>
        </div>

        {/* Student Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="flex-shrink-0">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-blue-600">
                  {student.firstName.charAt(0)}{student.lastName.charAt(0)}
                </span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">
                {student.firstName} {student.lastName}
              </h2>
              <p className="text-gray-600">
                {student.yearGroup} • {student.phase || 'Primary'} • {student.academicLevel || 'Mixed'}
              </p>
            </div>
          </div>
        </div>

        {/* Questionnaire Form */}
        <BranchingQuestionnaireForm
          onSubmit={handleSubmit}
          onBack={handleBack}
        />

        {submitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
                <span className="text-gray-700">Saving assessment...</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default TriagePage;